import { hero } from '@/state/tour';
import { saveBytes } from '@/store/download';
import { useHeroStore } from '@/store/store';
import { sortedStoreys } from './request';
import { Sheet } from './Sheet';
import { useDrawingsStore, type SectionAxis } from './state';

export function DrawingsPanel() {
  const status = useHeroStore((s) => s.status);
  const storeys = useHeroStore((s) => s.storeys);
  const s = useDrawingsStore();
  const rooms = s.drawing?.rooms.length ?? 0;
  const right =
    status === 'loading' || (status === 'ready' && !s.drawn)
      ? 'reading the IFC…'
      : status === 'error'
        ? 'the IFC did not load'
        : s.busy
          ? 'cutting…'
          : s.error
            ? s.error
            : s.drawn?.startsWith('plan')
              ? `${s.title} · ${rooms} rooms`
              : s.title || '–';

  const save = (ext: 'svg' | 'dxf') => {
    const d = s.drawing;
    if (!d) return;
    const text = ext === 'svg' ? d.svg : d.dxf();
    const name = `${hero.propsLayer}-${s.title.toLowerCase().replace(/\s+/g, '-')}.${ext}`;
    saveBytes(
      new TextEncoder().encode(text),
      name,
      ext === 'svg' ? 'image/svg+xml' : 'application/dxf',
    );
  };

  if (!hero.ifc)
    return (
      <div className="lens-panel drawings">
        <div className="lens-bar">
          <span className="lens-dot" />
          <span>drawings</span>
        </div>
        <p className="lens-foot">This building has no IFC to cut.</p>
      </div>
    );

  return (
    <div className="lens-panel drawings">
      <div className="lens-bar">
        <span className="lens-dot" />
        <span>drawings</span>
        <span className="lens-right">{right}</span>
      </div>
      <div className="drawing-controls">
        <div className="qty-bar" role="group" aria-label="View">
          <button type="button" aria-pressed={s.mode === 'plan'} onClick={() => s.setMode('plan')}>
            Plan
          </button>
          <button
            type="button"
            aria-pressed={s.mode === 'section'}
            onClick={() => s.setMode('section')}
          >
            Section
          </button>
        </div>
        {s.mode === 'plan' ? (
          <select
            value={s.storeyId ?? ''}
            onChange={(e) => s.setStorey(Number(e.target.value))}
            aria-label="Storey"
            disabled={!storeys.length}
          >
            {sortedStoreys(storeys).map((st) => (
              <option key={st.expressID} value={st.expressID}>
                {st.name}
              </option>
            ))}
          </select>
        ) : (
          <>
            <div className="qty-bar" role="group" aria-label="Axis">
              {(['x', 'z'] as SectionAxis[]).map((a) => (
                <button
                  key={a}
                  type="button"
                  aria-pressed={s.axis === a}
                  onClick={() => s.setAxis(a)}
                >
                  {a.toUpperCase()}
                </button>
              ))}
            </div>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={s.at}
              onChange={(e) => s.setAt(Number(e.target.value))}
              aria-label="Section position"
            />
          </>
        )}
      </div>
      <Sheet
        key={s.mode === 'plan' ? `plan:${s.storeyId}` : `section:${s.axis}`}
        drawing={s.drawing}
      />
      <div className="drawing-foot">
        <span>wheel to zoom, drag to pan</span>
        <button type="button" disabled={!s.drawing} onClick={() => save('svg')}>
          SVG
        </button>
        <button type="button" disabled={!s.drawing} onClick={() => save('dxf')}>
          DXF
        </button>
      </div>
    </div>
  );
}
